/**
 * US-65: модалка «Сменить пароль».
 *
 * 3 поля: старый / новый / подтверждение.
 * Inline-валидация на стороне клиента (длина 10+, минимум 1 буква и 1 цифра,
 * совпадение нового и подтверждения). Финальная валидация — на бэке через
 * ComplexityValidator.
 */

import { useState } from "react";
import { Modal, Stack, PasswordInput, Button, Alert, Text } from "@mantine/core";
import { AlertCircle, Check } from "lucide-react";

import { changePassword } from "../api/client";

interface Props {
  opened: boolean;
  onClose: () => void;
}

const MIN_LEN = 10;
const LETTER_RE = /[A-Za-zА-Яа-яЁё]/;
const DIGIT_RE = /\d/;

function validateNewPassword(pw: string): string | null {
  if (pw.length < MIN_LEN) return `Не менее ${MIN_LEN} символов (сейчас ${pw.length}).`;
  if (!LETTER_RE.test(pw)) return "Пароль должен содержать хотя бы одну букву.";
  if (!DIGIT_RE.test(pw)) return "Пароль должен содержать хотя бы одну цифру.";
  return null;
}

export function ChangePasswordModal({ opened, onClose }: Props) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  const localError = validateNewPassword(newPw);
  const mismatch = newPw && confirmPw && newPw !== confirmPw;
  const canSubmit = oldPw && newPw && confirmPw && !localError && !mismatch && !submitting;

  function reset() {
    setOldPw("");
    setNewPw("");
    setConfirmPw("");
    setServerError(null);
    setServerErrors([]);
    setSuccess(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    setServerError(null);
    setServerErrors([]);
    setSuccess(false);
    setSubmitting(true);
    try {
      await changePassword(oldPw, newPw);
      setSuccess(true);
    } catch (e) {
      const err = e as Error & { body?: { detail?: string; errors?: string[] } };
      const body = err.body;
      setServerError(body?.detail ?? err.message);
      setServerErrors(body?.errors ?? []);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Смена пароля" centered size="md">
      <Stack gap="md">
        {success && (
          <Alert color="green" icon={<Check size={16} />} data-testid="cpw-success">
            Пароль успешно изменён. Текущая сессия осталась активной.
          </Alert>
        )}
        <PasswordInput
          label="Старый пароль"
          value={oldPw}
          onChange={(e) => setOldPw(e.currentTarget.value)}
          required
          data-testid="cpw-old"
          autoComplete="current-password"
        />
        <PasswordInput
          label="Новый пароль"
          description={`Не менее ${MIN_LEN} символов, хотя бы одна буква и одна цифра.`}
          value={newPw}
          onChange={(e) => setNewPw(e.currentTarget.value)}
          error={newPw && localError ? localError : undefined}
          required
          data-testid="cpw-new"
          autoComplete="new-password"
        />
        <PasswordInput
          label="Подтвердите новый пароль"
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.currentTarget.value)}
          error={mismatch ? "Пароли не совпадают" : undefined}
          required
          data-testid="cpw-confirm"
          autoComplete="new-password"
        />
        {serverError && (
          <Alert color="red" icon={<AlertCircle size={16} />} data-testid="cpw-error">
            <Text fw={500}>{serverError}</Text>
            {serverErrors.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {serverErrors.map((e, i) => (
                  <li key={i}>
                    <Text size="sm">{e}</Text>
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        <Button
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
          loading={submitting}
          data-testid="cpw-submit"
        >
          Сменить пароль
        </Button>
      </Stack>
    </Modal>
  );
}
